import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Maximize, Minus, Plus } from "lucide-react";
import type { GameObject } from "../types";
import type { CanvasEngine, Camera, Resolvers } from "./engine";
export type CanvasHandle = {
  fit(): void;
  focus(ids: string[]): void;
  centre(): { x: number; y: number };
  toWorld(clientX: number, clientY: number): { x: number; y: number };
};
type Props = {
  projectId: string;
  objects: GameObject[];
  width: number;
  height: number;
  grid: boolean;
  selected: string[];
  readOnly: boolean;
  resolvers: Resolvers;
  onSelect(ids: string[]): void;
  onCommit(objects: GameObject[], moved: string[]): void;
  onOpen(id: string): void;
  onKeyDown(e: React.KeyboardEvent): void;
  onFiles(files: File[], at: { x: number; y: number }): void;
};
export const SceneCanvas = forwardRef<CanvasHandle, Props>(
  function SceneCanvas(props, ref) {
    const host = useRef<HTMLDivElement>(null),
      engine = useRef<CanvasEngine | null>(null),
      latest = useRef(props),
      [ready, setReady] = useState(false),
      [failed, setFailed] = useState(false),
      [camera, setCamera] = useState<Camera>({ x: 0, y: 0, zoom: 1 });
    latest.current = props;
    useEffect(() => {
      let alive = true,
        e: CanvasEngine | null = null;
      // PixiJS is loaded only when a scene editor opens.
      import("./engine")
        .then(async ({ CanvasEngine }) => {
          if (!alive) return;
          e = new CanvasEngine(
            host.current!,
            `/api/projects/${props.projectId}`,
            {
              select: (ids) => latest.current.onSelect(ids),
              commit: (objects, moved) =>
                latest.current.onCommit(objects, moved),
              camera: (c) => setCamera(c),
              open: (id) => latest.current.onOpen(id),
            },
          );
          engine.current = e;
          await e.init();
          if (!alive) return;
          const p = latest.current;
          e.readOnly = p.readOnly;
          e.setArea(p.width, p.height);
          e.setGrid(p.grid);
          e.setObjects(p.objects, p.resolvers);
          e.setSelection(p.selected);
          e.fit();
          setReady(true);
        })
        .catch(() => alive && setFailed(true));
      return () => {
        alive = false;
        e?.destroy();
        engine.current = null;
      };
    }, [props.projectId]);
    useEffect(() => {
      if (ready) engine.current?.setObjects(props.objects, props.resolvers);
    }, [ready, props.objects, props.resolvers]);
    useEffect(() => {
      if (ready) engine.current?.setSelection(props.selected);
    }, [ready, props.selected]);
    useEffect(() => {
      if (ready) engine.current?.setArea(props.width, props.height);
    }, [ready, props.width, props.height]);
    useEffect(() => {
      if (ready) engine.current?.setGrid(props.grid);
    }, [ready, props.grid]);
    useEffect(() => {
      if (engine.current) engine.current.readOnly = props.readOnly;
    }, [props.readOnly]);
    useImperativeHandle(
      ref,
      () => ({
        fit: () => engine.current?.fit(),
        focus(ids) {
          const e = engine.current;
          const list = latest.current.objects.filter((o) => ids.includes(o.id));
          if (!e || !list.length) return;
          const x = Math.min(...list.map((o) => o.x)),
            y = Math.min(...list.map((o) => o.y));
          e.fit({
            x,
            y,
            width: Math.max(...list.map((o) => o.x + o.width)) - x,
            height: Math.max(...list.map((o) => o.y + o.height)) - y,
          });
        },
        centre() {
          const e = engine.current,
            el = host.current;
          if (!e || !el) return { x: 100, y: 100 };
          return e.screenToWorld(el.clientWidth / 2, el.clientHeight / 2);
        },
        toWorld(clientX, clientY) {
          const e = engine.current,
            r = host.current!.getBoundingClientRect();
          return e
            ? e.screenToWorld(clientX - r.left, clientY - r.top)
            : { x: 100, y: 100 };
        },
      }),
      [],
    );
    return (
      <div className="scene-canvas-wrap">
        <div
          ref={host}
          className="scene-canvas"
          tabIndex={0}
          role="application"
          aria-label="Холст сцены"
          data-ready={ready ? "true" : "false"}
          onKeyDown={(e) => {
            if (e.code === "Space" && !e.repeat) {
              engine.current?.setSpace(true);
              e.preventDefault();
              return;
            }
            const plain = !e.ctrlKey && !e.metaKey && !e.altKey;
            if (e.shiftKey && plain && e.code === "Digit1") {
              engine.current?.fit();
              e.preventDefault();
              return;
            }
            if (e.shiftKey && plain && e.code === "Digit0") {
              const c = engine.current;
              c?.zoomAt(1 / (c.camera.zoom || 1));
              e.preventDefault();
              return;
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === "=" || e.key === "+")) {
              engine.current?.zoomAt(1.25);
              e.preventDefault();
              return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "-") {
              engine.current?.zoomAt(0.8);
              e.preventDefault();
              return;
            }
            props.onKeyDown(e);
          }}
          onKeyUp={(e) => {
            if (e.code === "Space") engine.current?.setSpace(false);
          }}
          onBlur={() => engine.current?.setSpace(false)}
          onDragOver={(e) => {
            if (!props.readOnly && e.dataTransfer.types.includes("Files"))
              e.preventDefault();
          }}
          onDrop={(e) => {
            const files = [...e.dataTransfer.files].filter((f) =>
              f.type.startsWith("image/"),
            );
            if (!files.length || props.readOnly) return;
            e.preventDefault();
            const r = host.current!.getBoundingClientRect(),
              at = engine.current?.screenToWorld(
                e.clientX - r.left,
                e.clientY - r.top,
              ) || { x: 100, y: 100 };
            props.onFiles(files, at);
          }}
        />
        {failed && (
          <div className="error-message scene-canvas-error" role="alert">
            Браузер не поддерживает WebGL, холст недоступен.
          </div>
        )}
        <div className="scene-zoom" role="group" aria-label="Масштаб холста">
          <button
            className="icon-button"
            aria-label="Отдалить"
            onClick={() => engine.current?.zoomAt(0.8)}
          >
            <Minus size={14} />
          </button>
          <button
            className="text-button"
            aria-label="Масштаб 100%"
            title="Shift+0"
            onClick={() => {
              const c = engine.current;
              c?.zoomAt(1 / (c.camera.zoom || 1));
            }}
          >
            {Math.round(camera.zoom * 100)}%
          </button>
          <button
            className="icon-button"
            aria-label="Приблизить"
            onClick={() => engine.current?.zoomAt(1.25)}
          >
            <Plus size={14} />
          </button>
          <button
            className="icon-button"
            aria-label="Показать всё"
            title="Shift+1"
            onClick={() => engine.current?.fit()}
          >
            <Maximize size={14} />
          </button>
        </div>
      </div>
    );
  },
);
